import { useEffect, useMemo, useRef, useState } from "react";
import { isMuted } from "@/utils/sound";

const LEAF_SRCS = ["/assets/seasonal/leaf-orange-1.png", "/assets/seasonal/leaf-orange-2.png"];
const TARANTULA = "/assets/seasonal/tarantula.png";
const BAT = "/assets/seasonal/bat.png";
const WITCH = "/assets/seasonal/witch.png";
const rand = (min, max) => min + Math.random() * (max - min);

function WebSvg({ className, style }) {
  return (
    <svg viewBox="0 0 150 150" className={className} style={style} aria-hidden="true">
      <path d="M0 0 L135 0" />
      <path d="M0 0 L117 68" />
      <path d="M0 0 L68 117" />
      <path d="M0 0 L0 135" />
      <path d="M45 0 Q34 9 39 22.5 Q25 25 22.5 39 Q9 34 0 45" />
      <path d="M90 0 Q69 18.5 78 45 Q50 50 45 78 Q18.5 69 0 90" />
      <path d="M135 0 Q103 28 117 67.5 Q76 76 67.5 117 Q28 103 0 135" />
    </svg>
  );
}

function StarSvg({ className, style }) {
  return (
    <svg viewBox="0 0 24 24" className={className} style={style} aria-hidden="true">
      <path d="M12 0 L14.5 9.5 L24 12 L14.5 14.5 L12 24 L9.5 14.5 L0 12 L9.5 9.5 Z" fill="#fef08a" />
    </svg>
  );
}

const GARLAND_COLORS = ["#ef4444", "#f59e0b", "#22c55e", "#3b82f6", "#eab308", "#ec4899"];

function SideGarland({ className, testId }) {
  const bulbs = Array.from({ length: 11 }, (_, i) => ({
    x: 30 + (i % 2 === 0 ? 8 : -8),
    y: 34 + i * 62,
    c: GARLAND_COLORS[i % GARLAND_COLORS.length],
    d: `${(i * 0.23).toFixed(2)}s`,
  }));
  return (
    <svg viewBox="0 0 60 720" className={className} data-testid={testId} aria-hidden="true">
      <path d="M30 2 C44 92, 16 172, 30 252 C44 332, 16 412, 30 492 C42 562, 20 642, 30 716"
        stroke="#3f6212" strokeWidth="3" fill="none" strokeLinecap="round" />
      {bulbs.map((b, i) => (
        <g key={i} className="amb-bulb" style={{ animationDelay: b.d }}>
          <line x1="30" y1={b.y - 2} x2={b.x} y2={b.y + 2} stroke="#3f6212" strokeWidth="1.6" />
          <circle cx={b.x} cy={b.y + 6} r="8.5" fill={b.c} opacity="0.25" />
          <circle cx={b.x} cy={b.y + 6} r="5.5" fill={b.c} />
        </g>
      ))}
    </svg>
  );
}

function playJingleBells() {
  if (isMuted()) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;
    for (let i = 0; i < 3; i += 1) {
      const t = now + i * 0.18;
      [5230, 6980, 8350].forEach((f, fi) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = f * (0.98 + Math.random() * 0.04);
        g.gain.setValueAtTime(0.0001, t);
        g.gain.exponentialRampToValueAtTime(0.05 / (fi + 1), t + 0.008);
        g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
        o.connect(g).connect(ctx.destination);
        o.start(t);
        o.stop(t + 0.2);
      });
    }
    setTimeout(() => ctx.close().catch(() => {}), 1500);
  } catch {}
}

function HalloweenAmbience() {
  const [pumpkins, setPumpkins] = useState([]);
  const [leafBurst, setLeafBurst] = useState(null);
  const [bats, setBats] = useState(null);
  const [witch, setWitch] = useState(null);

  // Vagues de 5-6 citrouilles errantes toutes les ~15 s
  useEffect(() => {
    let wave = 0;
    const spawn = () => {
      wave += 1;
      setPumpkins(Array.from({ length: 5 + Math.round(Math.random()) }, (_, i) => ({
        id: `${wave}-${i}`,
        left: rand(4, 86), top: rand(12, 66),
        x1: rand(-18, 18), y1: rand(-14, 14),
        x2: rand(-22, 22), y2: rand(-16, 16),
        size: rand(52, 92), dur: rand(12, 16), delay: rand(0, 2.5),
      })));
    };
    const t0 = setTimeout(spawn, 4000);
    const iv = setInterval(spawn, 15000);
    return () => { clearTimeout(t0); clearInterval(iv); };
  }, []);

  // Cycles : pluie de feuilles (3x, 1/min) -> 5 s de pause -> envol de chauves-souris -> sorcière (3x, 12 s)
  useEffect(() => {
    const timers = [];
    const CYCLE_STARTS = [6000, 66000, 126000];
    CYCLE_STARTS.forEach((start, ci) => {
      timers.push(setTimeout(() => {
        setLeafBurst({
          id: ci,
          leaves: Array.from({ length: 120 }, (_, i) => ({
            id: i, left: rand(0, 100), size: rand(18, 42),
            dur: rand(7, 13), delay: rand(0, 4),
            sway: rand(-12, 12), rot: rand(140, 420), src: LEAF_SRCS[i % 2],
          })),
        });
      }, start));
      timers.push(setTimeout(() => setLeafBurst((b) => (b && b.id === ci ? null : b)), start + 19000));
      // chauves-souris : 5 s après la fin de la pluie de feuilles — nuée effrayée, petite et nombreuse
      timers.push(setTimeout(() => {
        setBats({
          id: `b${ci}`,
          items: Array.from({ length: 15 }, (_, i) => ({
            id: i, left: rand(30, 60), top: rand(30, 55),
            dx: rand(-22, 22), dy: rand(-16, 12),
            r0: rand(-12, 12), r1: rand(-16, 16), r2: rand(-10, 10),
            size: rand(38, 62), dur: rand(3, 4.5), delay: rand(0, 0.7),
          })),
        });
      }, start + 24000));
      timers.push(setTimeout(() => setBats((b) => (b && b.id === `b${ci}` ? null : b)), start + 34000));
      // sorcière : 3 apparitions espacées de 12 s, ~6 s chacune
      [34000, 46000, 58000].forEach((off, ai) => {
        timers.push(setTimeout(() => setWitch({ id: `w${ci}-${ai}` }), start + off));
        timers.push(setTimeout(() => setWitch((w) => (w && w.id === `w${ci}-${ai}` ? null : w)), start + off + 6200));
      });
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="amb-layer" data-testid="ambience-halloween" aria-hidden="true">
      {/* Toiles d'araignée : haut-droite, puis +2 s haut-gauche, puis +2 s milieu ; cycle de 64 s */}
      <div className="amb-web amb-web-tr" style={{ "--wd": "6s" }} data-testid="web-tr"><WebSvg /></div>
      <div className="amb-web amb-web-tl" style={{ "--wd": "8s" }} data-testid="web-tl"><WebSvg /></div>
      <div className="amb-web amb-web-mid" style={{ "--wd": "10s" }} data-testid="web-mid">
        <WebSvg style={{ position: "absolute", left: 0, top: 0, width: "150px", height: "150px" }} />
        <WebSvg style={{ position: "absolute", left: "-150px", top: 0, width: "150px", height: "150px", transform: "scaleX(-1)" }} />
      </div>
      {pumpkins.map((p) => (
        <img key={p.id} src="/assets/seasonal/pumpkin-glow.png" alt="" className="amb-pumpkin"
          style={{
            left: `${p.left}vw`, top: `${p.top}vh`, width: `${p.size}px`,
            "--x1": `${p.x1}vw`, "--y1": `${p.y1}vh`, "--x2": `${p.x2}vw`, "--y2": `${p.y2}vh`,
            "--dur": `${p.dur}s`, animationDelay: `${p.delay}s`,
          }} />
      ))}
      {/* Tarentule descendant le long de son fil (position centrale) */}
      <div className="amb-spider-drop" data-testid="amb-spider-drop">
        <span className="amb-spider-thread" />
        <img src={TARANTULA} alt="" className="amb-spider-img" />
      </div>
      {/* 3 tarentules qui circulent sur tout l'écran */}
      <div className="amb-spider-roam amb-roam-a" data-testid="amb-spider-roam-a">
        <img src={TARANTULA} alt="" />
      </div>
      <div className="amb-spider-roam amb-roam-b" data-testid="amb-spider-roam-b">
        <img src={TARANTULA} alt="" />
      </div>
      <div className="amb-spider-roam amb-roam-c" data-testid="amb-spider-roam-c">
        <img src={TARANTULA} alt="" />
      </div>
      {leafBurst && leafBurst.leaves.map((l) => (
        <img key={`${leafBurst.id}-${l.id}`} src={l.src} alt="" className="amb-leaf"
          style={{
            left: `${l.left}vw`, width: `${l.size}px`,
            "--dur": `${l.dur}s`, "--sway": `${l.sway}vw`, "--rot": `${l.rot}deg`,
            animationDelay: `${l.delay}s`,
          }} />
      ))}
      {bats && bats.items.map((b) => (
        <div key={`${bats.id}-${b.id}`} className="amb-bat" data-testid="amb-bat"
          style={{
            left: `${b.left}vw`, top: `${b.top}vh`, width: `${b.size}px`,
            "--dx": `${b.dx}vw`, "--dy": `${b.dy}vh`,
            "--r0": `${b.r0}deg`, "--r1": `${b.r1}deg`, "--r2": `${b.r2}deg`,
            "--dur": `${b.dur}s`, animationDelay: `${b.delay}s`,
          }}>
          <img src={BAT} alt="" className="amb-bat-img" />
        </div>
      ))}
      {witch && (
        <div key={witch.id} className="amb-witch" data-testid="amb-witch">
          <img src={WITCH} alt="" className="w-full h-auto" />
        </div>
      )}
    </div>
  );
}

function NoelAmbience() {
  const [showGarlands, setShowGarlands] = useState(false);

  const stars = useMemo(() => Array.from({ length: 18 }, (_, i) => ({
    id: i, left: rand(2, 96), top: rand(2, 38), size: rand(10, 22), delay: rand(0, 2.8),
  })), []);
  const sparkles = useMemo(() => Array.from({ length: 20 }, (_, i) => ({
    id: i, left: rand(0, 95), size: rand(8, 16), dur: rand(10, 18), delay: rand(0, 16), sway: rand(-10, 10),
  })), []);

  // Guirlandes : 7 s visibles toutes les 15 s x3, puis 1 min de pause, cycle recommencé
  useEffect(() => {
    let cancelled = false;
    const timers = [];
    const cycle = () => {
      for (let i = 0; i < 3; i += 1) {
        timers.push(setTimeout(() => { if (!cancelled) setShowGarlands(true); }, i * 15000));
        timers.push(setTimeout(() => { if (!cancelled) setShowGarlands(false); }, i * 15000 + 7000));
      }
      timers.push(setTimeout(() => { if (!cancelled) cycle(); }, 3 * 15000 + 60000));
    };
    timers.push(setTimeout(cycle, 8000));
    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, []);

  // Grelots : 5 tintements espacés de 8 s, puis 45 s de pause, 3 cycles
  useEffect(() => {
    const timers = [];
    for (let round = 0; round < 3; round += 1) {
      const base = round * 77000;
      [0, 8, 16, 24, 32].forEach((off) => timers.push(setTimeout(playJingleBells, base + off * 1000)));
    }
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className="amb-layer" data-testid="ambience-noel" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <span key={`shoot-${i}`} className="amb-shooting-star"
          style={{ "--ss-delay": `${3 + i * 9}s`, "--ss-top": `${7 + i * 9}vh` }} />
      ))}
      {stars.map((s) => (
        <StarSvg key={s.id} className="amb-star"
          style={{ left: `${s.left}vw`, top: `${s.top}vh`, width: `${s.size}px`, "--delay": `${s.delay}s` }} />
      ))}
      {sparkles.map((s) => (
        <StarSvg key={`sp-${s.id}`} className="amb-sparkle"
          style={{
            left: `${s.left}vw`, width: `${s.size}px`,
            "--dur": `${s.dur}s`, "--delay": `${s.delay}s`, "--sway": `${s.sway}vw`,
          }} />
      ))}
      <SideGarland testId="garland-left"
        className={`amb-garland-svg amb-garland-left${showGarlands ? " on" : ""}`} />
      <SideGarland testId="garland-right"
        className={`amb-garland-svg amb-garland-right${showGarlands ? " on" : ""}`} />
    </div>
  );
}

export default function SeasonalAmbience({ mode }) {
  if (mode === "halloween") return <HalloweenAmbience />;
  if (mode === "noel") return <NoelAmbience />;
  return null;
}
